import React, { useCallback, useMemo } from 'react';
import {
  FormControl,
  FormErrorIcon,
  FormErrorMessage,
  FormLabel,
  Text,
} from '@chakra-ui/react';
import { T } from '@transifex/react';
import {
  ProductAccessOptions,
  ProductAccessRowStateType,
} from '../../../../common/components/ProductAccessOptions/ProductAccessOptions';
import { useGetOrganizationTenantsQuery } from 'gql/graphql';
import { APP_ACCESS_REQUIRED_MSG, transformTenants } from '../utils';
import { AppAccessFieldProps } from '../types';
import { env } from 'features/common/config/envConfig';
import { useFeature } from 'lib/featureFlags/features';

export const AppAccessField = ({
  productAccessState,
  setProductAccessState,
  tenantList,
  showAppAccessError = false,
}: AppAccessFieldProps) => {
  const { data: tenantsData, loading: tenantsLoading } =
    useGetOrganizationTenantsQuery({
      variables: { first: 100, env },
    });

  const isScaiDamEnabled = useFeature('SCAI_DAM');

  // Transform tenants data for ProductAccessOptions component
  const transformedTenants = useMemo(
    () => transformTenants(tenantsData, isScaiDamEnabled),
    [tenantsData, isScaiDamEnabled],
  );

  // Get available app options (excluding already selected ones and existing group data)
  const getAvailableOptions = useCallback(() => {
    const productIds = productAccessState.map((p) => p.product.id);
    const groupDataTenantIds = tenantList?.map((g) => g.tenantId) || [];

    const filteredProductIds = transformedTenants?.filter(
      (p) => !productIds.includes(p.id) && !groupDataTenantIds.includes(p.id),
    );
    return filteredProductIds || [];
  }, [transformedTenants, productAccessState, tenantList]);

  const hasTenants = !!transformedTenants?.length;

  const setProductAccessStateCb = useCallback(
    (data: ProductAccessRowStateType) => setProductAccessState(data),
    [setProductAccessState],
  );

  return (
    <FormControl py={2} isInvalid={showAppAccessError && hasTenants}>
      <FormLabel>
        <T _str='App access' />
      </FormLabel>
      {hasTenants ? (
        <ProductAccessOptions
          isLoading={tenantsLoading}
          isMarketplaceAppLoading={false} // ToDo: Check if we need to load marketplace apps
          productAccessOptions={getAvailableOptions()}
          productAccessRowState={productAccessState}
          setproductAccessRowState={setProductAccessStateCb}
        />
      ) : (
        <Text
          fontSize='md'
          color='chakra-subtle-text'
          data-testid='create-team-drawer-no-apps-text'
        >
          No apps are set up yet. When an app is available, you must update the
          access rights of users.
        </Text>
      )}
      {showAppAccessError && hasTenants && (
        <FormErrorMessage data-testid='app-access-error-message'>
          <FormErrorIcon />
          {APP_ACCESS_REQUIRED_MSG}
        </FormErrorMessage>
      )}
    </FormControl>
  );
};
